import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ReactNode } from "react";
// `Breakpoint` comes from the package root, like MHidden.tsx does it — the
// `styles/createBreakpoints` path is a v4 layout and does not resolve in 5.0.0-beta.4.
import { useMediaQuery, useTheme } from "@material-ui/core";
import type { Breakpoint } from "@material-ui/core";

/**
 * How wide the surface a contact's detail content is rendered INTO actually is.
 *
 * ── The bug this exists to remove ─────────────────────────────────────────────────
 *
 * The 10-tab contact detail was written for a full page, so it asks the VIEWPORT how
 * much room it has: ten `useMediaQuery(theme.breakpoints.down(...))` calls across eight
 * files. That was correct while `/dashboard/contacts/:id` was its only host.
 *
 * The CRM side panel then began rendering the same content in a 420–460px column
 * (`PANEL_WIDTH` in `CrmWorkspace`). On a 1920 screen every one of those queries answers
 * about the 1920, so the content lays itself out for a full page and is then painted into
 * roughly a fifth of one. Reported as the panel "rendering as full width inside a max
 * width container — we see kind of the continuity of it", which is exactly right: the
 * layout genuinely does continue past the edge of the box.
 *
 * It is the same mistake `crmTablePlan.ts` was written to fix one level up, and it has the
 * same shape: THE PANEL IS A SIBLING FLEX ITEM, so opening it changes the container and
 * leaves the viewport untouched. No viewport query can see that.
 *
 * ── Why a measured width and not a boolean ────────────────────────────────────────
 *
 * The provider could have published `isNarrow` and been done. It publishes the width
 * instead so that `useContactSurfaceDown` can answer for ANY breakpoint against real
 * pixel values — `down("sm")` and `down("lg")` are both asked for by the existing call
 * sites, and they must not collapse into one flag. It also means a panel that later
 * becomes user-resizable is already correct, with nothing to revisit.
 *
 * ── The rule for call sites ───────────────────────────────────────────────────────
 *
 * Content rendered INSIDE the surface asks {@link useContactSurfaceDown}. Anything
 * PORTALLED OUT of it — a Dialog, a Popover, a Menu — must keep asking the viewport,
 * because that is the box it is really painted into. `ListingQuickViewDialog`'s
 * `fullScreen` is deliberately left on `useMediaQuery` for exactly this reason: it is a
 * dialog over the whole window, and a panel-width answer would make it full-screen on a
 * 1920 monitor.
 */

type ContactSurface = {
  /** Measured content width of the host element, in px. */
  readonly width: number;
};

/**
 * Absent by default, and that absence IS the full-page behaviour — every hook below
 * falls back to the viewport, so the contact page is byte-identical to before this file
 * existed and no host that does not opt in can be affected.
 */
const ContactSurfaceContext = createContext<ContactSurface | null>(null);

/** Coarse enough to absorb a drag-resize, far finer than any breakpoint gap. */
const WIDTH_QUANTUM = 8;

export type ContactSurfaceMeasure = {
  /** Put this on the element the detail content is rendered inside. */
  surfaceRef: (node: HTMLElement | null) => void;
  /** Wrap the content in this. Renders no DOM of its own. */
  ContactSurfaceProvider: (props: { children: ReactNode }) => JSX.Element;
};

/**
 * Measures a host element and publishes its width to the detail content inside it.
 *
 * ResizeObserver via a CALLBACK REF, not `useRef` + `useEffect`: the effect can fire
 * before the node exists on the first render and would then observe nothing. Not
 * `useElementSize` from usehooks-ts either — that one is a window `resize` listener, so
 * it never fires when the panel opens, which is the only event that matters here.
 */
export function useContactSurface(): ContactSurfaceMeasure {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const surfaceRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      const quantised = Math.round(measured / WIDTH_QUANTUM) * WIDTH_QUANTUM;
      // Guarded so a sub-quantum jitter cannot re-render the whole tab body.
      setWidth((current) => (current === quantised ? current : quantised));
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  /**
   * ⚠️ THE PROVIDER COMPONENT'S IDENTITY MUST NEVER CHANGE, and the value therefore rides a
   * ref rather than the closure.
   *
   * React reconciles by element TYPE, and for a function component the type is the function
   * reference. So a `useCallback(…, [value])` provider is a NEW type on the render where the
   * width lands — which is every mount, since the width starts at 0 and the ResizeObserver
   * fires a beat later. React then unmounts the entire subtree and mounts a fresh one:
   * every hook re-runs, every piece of local state is lost, and any in-flight request is
   * abandoned and re-issued. The host's own content is the thing being thrown away, which is
   * the exact opposite of what measuring it was for.
   *
   * With `[]` deps the type is stable, so React UPDATES instead. The host re-renders when its
   * own `width` state changes, this component re-runs, it reads the ref that was refreshed
   * during that same render, and consumers see the new value — no remount anywhere.
   */
  const valueRef = useRef<ContactSurface>({ width: 0 });
  if (valueRef.current.width !== width) valueRef.current = { width };

  const ContactSurfaceProvider = useCallback(
    ({ children }: { children: ReactNode }) => (
      <ContactSurfaceContext.Provider value={valueRef.current}>{children}</ContactSurfaceContext.Provider>
    ),
    []
  );

  return { surfaceRef, ContactSurfaceProvider };
}

/**
 * `theme.breakpoints.down(bp)` for the SURFACE, falling back to the viewport.
 *
 * ⚠️ BOTH HOOKS ARE CALLED UNCONDITIONALLY, every render, and then one result is chosen.
 * Writing this as `surface ? … : useMediaQuery(…)` would stop calling a hook the moment a
 * host provided a surface, which is a hooks-order crash rather than a layout bug — and it
 * is a mistake this codebase has already made once, in `ContactDetailTabs`.
 *
 * The width is 0 until the first observation. That reads as "not yet measured", so it
 * defers to the viewport for one frame rather than claiming the surface is 0px wide and
 * flashing the narrowest possible layout before settling.
 */
export function useContactSurfaceDown(bp: Breakpoint): boolean {
  const surface = useContext(ContactSurfaceContext);
  const theme = useTheme();
  const viewportAnswer = useMediaQuery(theme.breakpoints.down(bp));

  if (!surface || surface.width === 0) return viewportAnswer;
  // Verified against the installed implementation rather than assumed: MUI v5's
  // `down(key)` emits `max-width: values[key] - 0.05px`, so it is true exactly when the
  // width is below that key's own value. (v4's `down` was inclusive of the breakpoint
  // ABOVE — this package is named @material-ui but is 5.0.0-beta.4, so the v5 rule is the
  // one that applies.) Reading `theme.breakpoints.values` rather than hardcoding 600/900
  // keeps this correct if the theme ever overrides them.
  return surface.width < theme.breakpoints.values[bp];
}

/** True when the detail content is inside a measured surface at all. */
export function useIsInContactSurface(): boolean {
  return useContext(ContactSurfaceContext) !== null;
}
